import React, { useRef } from 'react';

import { Box, Button, Group, List, Text, Title, createStyles } from '@mantine/core';
import { Dropzone, FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import { IconCircleX, IconCloudUpload, IconDownload } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import {
  MAX_FILE_COUNT,
  MAX_TOTAL_BYTE_SIZE,
  MAX_VIDEO_DURATION,
  NUM_BYTES_IN_MEGABYTE
} from 'shared/constants/general-constants';
import { PRIMARY_SHADE_WHITE } from 'shared/constants/theme';
import { Resource } from 'shared/interfaces/Resource';

const DROP_ZONE_ICON_SIZE = 40;

const useStyles = createStyles(() => ({
  dropzone: {
    borderWidth: 1,
    paddingBottom: 50
  }
}));

interface FileDropzoneProps {
  resourceToUpdate?: Resource;
  closeFileDropzone?: () => void;
  onFilesDrop?: (files: FileWithPath[]) => void;
}

export function FileDropzone({ resourceToUpdate, closeFileDropzone, onFilesDrop }: FileDropzoneProps) {
  const { classes, theme } = useStyles();
  const openRef = useRef<() => void>(null);
  const { t } = useTranslation();

  const checkVideoDuration = (file: FileWithPath): Promise<void> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        video.remove();
        window.URL.revokeObjectURL(video.src);
        const duration = video.duration;
        if (duration > MAX_VIDEO_DURATION) {
          NotificationService.showResult(
            false,
            t('FILE_DROPZONE.VIDEO_TOO_LONG', { maxVideoDuration: (MAX_VIDEO_DURATION / 60).toFixed(0) })
          );
          reject();
        } else {
          resolve();
        }
      };
      video.onerror = (error) => {
        NotificationService.showResult(false, t('FILE_DROPZONE.ERROR_LOAD_VIDEO'));
        reject(error);
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const onDrop = async (files: FileWithPath[]) => {
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);
    if (totalSize > MAX_TOTAL_BYTE_SIZE) {
      NotificationService.showResult(false, t('FILE_DROPZONE.TOTAL_SIZE_TOO_LARGE'));
      if (closeFileDropzone) closeFileDropzone();
      return;
    }
    for (const file of files) {
      if (file.type === MIME_TYPES.mp4) {
        try {
          await checkVideoDuration(file);
        } catch (error) {
          if (closeFileDropzone) closeFileDropzone();
          return;
        }
      }
    }

    if (onFilesDrop) onFilesDrop(files);
    if (closeFileDropzone) closeFileDropzone();
    return;
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Dropzone
        style={{ position: 'relative' }}
        openRef={openRef}
        onDrop={(files: FileWithPath[]) => onDrop(files)}
        onReject={() => NotificationService.showResult(false, t('FILE_DROPZONE.REJECT'))}
        className={classes.dropzone}
        accept={[MIME_TYPES.pdf, MIME_TYPES.mp4]}
        multiple={!resourceToUpdate}
        maxFiles={MAX_FILE_COUNT}
        maxSize={MAX_TOTAL_BYTE_SIZE}>
        <Group position="center">
          <Dropzone.Accept>
            <IconDownload style={{ height: DROP_ZONE_ICON_SIZE, width: DROP_ZONE_ICON_SIZE }} />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconCircleX
              style={{ height: DROP_ZONE_ICON_SIZE, width: DROP_ZONE_ICON_SIZE }}
              color={theme.colors.red[PRIMARY_SHADE_WHITE]}
            />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconCloudUpload style={{ height: DROP_ZONE_ICON_SIZE, width: DROP_ZONE_ICON_SIZE }} />
          </Dropzone.Idle>
        </Group>

        <Title align="center" order={3} mt="xl">
          <Dropzone.Accept>
            {t(resourceToUpdate ? 'FILE_DROPZONE.ACCEPT.UPDATE' : 'FILE_DROPZONE.ACCEPT.UPLOAD')}
          </Dropzone.Accept>
          <Dropzone.Reject>{t('FILE_DROPZONE.REJECT')}</Dropzone.Reject>
          <Dropzone.Idle>
            {!resourceToUpdate
              ? t('FILE_DROPZONE.IDLE.UPLOAD')
              : t('FILE_DROPZONE.IDLE.UPDATE', { fileName: resourceToUpdate.fileName })}
          </Dropzone.Idle>
        </Title>
        <Text align="center" size="sm" mt="xs" color="dimmed">
          {t(resourceToUpdate ? 'FILE_DROPZONE.MESSAGE_UPDATE' : 'FILE_DROPZONE.MESSAGE_UPLOAD')}
        </Text>
        <List type="unordered" size="sm" center={true}>
          <List.Item>
            <Text align="center" size="sm" mt="xs" color="dimmed">
              {t('FILE_DROPZONE.FILE_FORMAT_HINT')}
            </Text>
          </List.Item>
          {!resourceToUpdate && (
            <List.Item>
              <Text align="center" size="sm" mt="xs" color="dimmed">
                {t('FILE_DROPZONE.FILE_COUNT_HINT', { maxFileCount: MAX_FILE_COUNT })}
              </Text>
            </List.Item>
          )}
          <List.Item>
            <Text align="center" size="sm" mt="xs" color="dimmed">
              {t('FILE_DROPZONE.FILE_SIZE_HINT', { maxTotalFileByteSize: MAX_TOTAL_BYTE_SIZE / NUM_BYTES_IN_MEGABYTE })}
            </Text>
          </List.Item>
          <List.Item>
            <Text align="center" size="sm" mt="xs" color="dimmed">
              {t('FILE_DROPZONE.VIDEO_DURATION_HINT', { maxVideoDuration: (MAX_VIDEO_DURATION / 60).toFixed(0) })}
            </Text>
          </List.Item>
        </List>
      </Dropzone>

      <Button mt={-25} w={260} onClick={() => openRef.current?.()}>
        {t(resourceToUpdate ? 'FILE_DROPZONE.SELECT_FILE_UPDATE' : 'FILE_DROPZONE.SELECT_FILES_UPLOAD')}
      </Button>
    </Box>
  );
}
